import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { PROFILE_ROUTES, ROUTES } from 'configs';
import { selectCurrentUserSubscription } from 'store/selectors/subscriptions';
import { UserSubscription } from 'types/subscription';
import { getBillingAttentionActionUrl } from 'utils/subscriptionBilling';
import {
  getBillingAttentionReason,
  BillingAttentionReason,
} from 'utils/subscriptionState';

import {
  BannerActions,
  BannerAccent,
  BannerBar,
  BannerBody,
  BannerCloseButton,
  BannerCopy,
  BannerCta,
  BannerInner,
  BannerRoot,
  BannerSecondary,
  BannerTitle,
} from './styled';

const subscriptionPath = `${ROUTES.PROFILE}/${PROFILE_ROUTES.SUBSCRIPTION}`;

const DISMISS_STORAGE_PREFIX = 'gumb:billing-attention:dismissed:';

const copyKey = (
  reason: BillingAttentionReason,
  part: 'title' | 'body' | 'cta',
) => `subscriptions.billingAttention.${reason}.${part}`;

function getDismissStorageKey(
  subscription: UserSubscription,
  reason: BillingAttentionReason,
): string {
  const subId = subscription.subscriptionId ?? subscription.id;
  const stripeStatus = subscription.billingSubscriptionStatus ?? 'unknown';
  return `${DISMISS_STORAGE_PREFIX}${subId}:${stripeStatus}:${reason}`;
}

function isDismissedInSession(
  subscription: UserSubscription | null | undefined,
  reason: BillingAttentionReason | null,
): boolean {
  if (!subscription || !reason) return false;
  try {
    return (
      sessionStorage.getItem(getDismissStorageKey(subscription, reason)) === '1'
    );
  } catch {
    return false;
  }
}

const CloseIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden focusable="false">
    <path
      d="M2.1 2.1l9.8 9.8M11.9 2.1L2.1 11.9"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

const BillingAttentionBanner: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const subscription = useSelector(selectCurrentUserSubscription);

  const reason = useMemo(
    () => getBillingAttentionReason(subscription),
    [subscription],
  );

  const [dismissed, setDismissed] = useState(() =>
    isDismissedInSession(subscription, reason),
  );

  useEffect(() => {
    setDismissed(isDismissedInSession(subscription, reason));
  }, [
    subscription,
    reason,
    subscription?.subscriptionId,
    subscription?.billingSubscriptionStatus,
    subscription?.updatedAt,
  ]);

  const actionUrl = useMemo(
    () =>
      getBillingAttentionActionUrl(
        subscription ?? undefined,
        reason ?? undefined,
      ),
    [subscription, reason],
  );

  const openExternal = useCallback((url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  }, []);

  const handlePrimary = useCallback(() => {
    if (actionUrl) {
      openExternal(actionUrl);
      return;
    }
    navigate(subscriptionPath);
  }, [actionUrl, openExternal, navigate]);

  const handleSecondary = useCallback(() => {
    navigate(subscriptionPath);
  }, [navigate]);

  const handleDismiss = useCallback(() => {
    if (!subscription || !reason) return;
    try {
      sessionStorage.setItem(getDismissStorageKey(subscription, reason), '1');
    } catch {
      // ignore storage errors
    }
    setDismissed(true);
  }, [subscription, reason]);

  if (!reason || dismissed) return null;

  const primaryLabel = actionUrl
    ? t(copyKey(reason, 'cta'))
    : t('subscriptions.billingAttention.viewSubscription');

  return (
    <BannerRoot role="alert" data-billing-attention={reason}>
      <BannerInner>
        <BannerAccent>
          <BannerBar aria-hidden />
          <BannerCopy>
            <BannerTitle>{t(copyKey(reason, 'title'))}</BannerTitle>
            <BannerBody>{t(copyKey(reason, 'body'))}</BannerBody>
          </BannerCopy>
        </BannerAccent>
        <BannerActions>
          <BannerCta type="button" onClick={handlePrimary}>
            {primaryLabel}
          </BannerCta>
          {actionUrl ? (
            <BannerSecondary type="button" onClick={handleSecondary}>
              {t('subscriptions.billingAttention.viewSubscription')}
            </BannerSecondary>
          ) : null}
        </BannerActions>
        <BannerCloseButton
          type="button"
          onClick={handleDismiss}
          aria-label={t('subscriptions.billingAttention.dismiss')}
        >
          <CloseIcon />
        </BannerCloseButton>
      </BannerInner>
    </BannerRoot>
  );
};

export default BillingAttentionBanner;
